import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  HostListener,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { AuthService } from '../../core/services/auth.service';
import { CookieService } from 'ngx-cookie-service';
import { LanguageService } from '../../core/services/language.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LayoutStore } from '../../store/layout.store';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { SimplebarAngularModule } from 'simplebar-angular';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';

import { LocalVars } from '@core/constant/local-storage.constants';
import { LocalStorageService } from '@core/services/local-storage.service';
import { BaseService } from '@core/services/base.service';
import { AUTH } from '@core/constant/api.constants';
import { PermissionService } from '@core/services/permission.service';
import { ToastService } from '@core/services/toast.service';
import { CART_PERMISSIONS } from '@features/cart-order-management/cart/models/cart.models';
import { NO_CART_MODE_MESSAGE } from '@features/order-management/models/saved-cart.models';
import { SavedCartService } from '@features/order-management/services/saved-cart.service';

interface LangItem {
  text: string;
  flag: string;
  lang: string;
}

@Component({
  selector: 'app-topbar',
  templateUrl: './topbar.component.html',
  styleUrls: ['./topbar.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    BsDropdownModule,
    SimplebarAngularModule,
    RouterLink,
    RouterLinkActive,
  ],
})
export class TopbarComponent implements OnInit {
  private readonly document = inject(DOCUMENT);
  private readonly authService = inject(AuthService);
  readonly languageService = inject(LanguageService);
  readonly translate = inject(TranslateService);
  readonly _cookiesService = inject(CookieService);
  readonly layoutStore = inject(LayoutStore);
  private readonly ls = inject(LocalStorageService);
  private readonly baseService = inject(BaseService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(PermissionService);
  private readonly savedCartService = inject(SavedCartService);
  private readonly toast = inject(ToastService);

  /** The header's Create Cart button — the screen's most important action, open to every role. */
  readonly canCreateCart = computed(() =>
    this.permissionService.hasPermission(CART_PERMISSIONS.CREATE),
  );
  /** One click, one cart: `createCart` mints a fresh idempotency key per call. */
  readonly creatingCart = signal(false);

  /**
   * Claims the cart before leaving, so a refused creation keeps the user where they are rather than
   * dropping them on a builder with no cart id. Mirrors `SavedCartsList.onCreateCart`.
   */
  async createCart(): Promise<void> {
    if (this.creatingCart()) return;
    this.creatingCart.set(true);
    const stage = 'contract';
    try {
      const roleId = await this.savedCartService.resolveDefaultRoleId();
      const cart = await this.savedCartService.createCart(stage, roleId);
      await this.router.navigate(['/order-management/edit-cart', cart.id], {
        queryParams: { stage },
      });
    } catch (err) {
      // The interceptor has already toasted an API refusal; a rep with no cart mode never reached it.
      if (err instanceof Error && err.message === NO_CART_MODE_MESSAGE) {
        this.toast.show(NO_CART_MODE_MESSAGE, 'error');
      }
    } finally {
      this.creatingCart.set(false);
    }
  }

  /** Highlights the "Profile" dropdown item for both /profile/view and /profile/update,
   *  since the update screen is conceptually part of the same Profile entry. */
  get isProfileActive(): boolean {
    const url = this.router.url.split('?')[0]?.split('#')[0] ?? '';
    return url === '/profile/view' || url === '/profile/update';
  }
  mode: string | undefined;
  element: HTMLElement | null = null;
  cookieValue = '';
  flagvalue: string | undefined;
  countryName: string | string[] = '';
  valueset: string | undefined;
  theme: string | undefined;
  layout = 'vertical';
  userName = '';
  profileImage: string | null = null;
  readonly fallbackProfileImage = 'assets/images/users/user-dummy-img.jpg';

  listLang: LangItem[] = [
    { text: 'English', flag: 'assets/images/flags/us.jpg', lang: 'en' },
    { text: 'Spanish', flag: 'assets/images/flags/spain.jpg', lang: 'es' },
    { text: 'German', flag: 'assets/images/flags/germany.jpg', lang: 'de' },
    { text: 'Italian', flag: 'assets/images/flags/italy.jpg', lang: 'it' },
    { text: 'Russian', flag: 'assets/images/flags/russia.jpg', lang: 'ru' },
  ];

  openMobileMenu = false;

  @Output() settingsButtonClicked = new EventEmitter();
  @Output() mobileMenuButtonClicked = new EventEmitter();

  ngOnInit() {
    this.theme = this.layoutStore.mode();
    this.openMobileMenu = false;
    this.element = document.documentElement;

    this.refreshUserName();
    void this.loadProfile();

    this.cookieValue = this._cookiesService.get('lang');
    const val = this.listLang.filter((langItem) => langItem.lang === this.cookieValue);
    this.countryName = val.map((langItem) => langItem.text);
    if (val.length === 0) {
      if (this.flagvalue === undefined) {
        this.valueset = 'assets/images/flags/us.jpg';
      }
    } else {
      this.flagvalue = val.map((langItem) => langItem.flag)[0];
    }
  }

  private async loadProfile(): Promise<void> {
    try {
      const raw = await this.baseService.callGetApi(AUTH.PROFILE);
      const res = raw as {
        data?: {
          first_name?: string | null;
          last_name?: string | null;
          profile_image?: string | null;
        } | null;
      } | null;
      const data = res?.data;
      if (!data) return;

      const fullName = [data.first_name, data.last_name].filter(Boolean).join(' ');
      if (fullName.length > 0) this.userName = fullName;
      this.profileImage = data.profile_image ?? null;

      const stored = this.ls.getItem(LocalVars.USER);
      const user = stored ? (JSON.parse(stored) as Record<string, unknown>) : {};
      user['first_name'] = data.first_name ?? user['first_name'];
      user['last_name'] = data.last_name ?? user['last_name'];
      user['profile_image'] = data.profile_image ?? null;
      this.ls.setItem(LocalVars.USER, JSON.stringify(user));
    } catch {
      // Leave the localStorage-sourced values in place.
    }
  }

  @HostListener('window:storage')
  @HostListener('window:user-profile-updated')
  refreshUserName(): void {
    const userDetails = this.ls.getItem(LocalVars.USER);
    if (!userDetails) {
      this.userName = '';
      this.profileImage = null;
      return;
    }

    const user = JSON.parse(userDetails) as {
      first_name?: string;
      last_name?: string;
      profile_image?: string | null;
    };
    this.userName = [user.first_name, user.last_name].filter(Boolean).join(' ');
    this.profileImage = user.profile_image ?? null;
  }

  onProfileImageError(event: Event): void {
    const img = event.target as HTMLImageElement | null;
    if (img && img.getAttribute('src') !== this.fallbackProfileImage) {
      img.src = this.fallbackProfileImage;
    }
  }

  get displayUserName(): string {
    return this.userName.length > 20 ? this.userName.slice(0, 20) + '...' : this.userName;
  }

  setLanguage(text: string, lang: string, flag: string) {
    this.countryName = text;
    this.flagvalue = flag;
    this.cookieValue = lang;
    this.languageService.setLanguage(lang);
  }

  toggleRightSidebar() {
    this.settingsButtonClicked.emit();
  }

  toggleMobileMenu(event: Event) {
    event.preventDefault();
    this.mobileMenuButtonClicked.emit();
  }

  async logout(): Promise<void> {
    await this.authService.logout();
  }

  fullscreen() {
    document.body.classList.toggle('fullscreen-enable');
    if (
      !document.fullscreenElement &&
      !(this.element as unknown as { mozFullScreenElement?: unknown })?.mozFullScreenElement &&
      !(this.element as unknown as { webkitFullscreenElement?: unknown })?.webkitFullscreenElement
    ) {
      if (this.element?.requestFullscreen) {
        void this.element.requestFullscreen();
      } else if (
        (this.element as unknown as { mozRequestFullScreen?: () => void })?.mozRequestFullScreen
      ) {
        (this.element as unknown as { mozRequestFullScreen: () => void }).mozRequestFullScreen();
      } else if (
        (this.element as unknown as { webkitRequestFullscreen?: () => void })
          ?.webkitRequestFullscreen
      ) {
        (
          this.element as unknown as { webkitRequestFullscreen: () => void }
        ).webkitRequestFullscreen();
      } else if (
        (this.element as unknown as { msRequestFullscreen?: () => void })?.msRequestFullscreen
      ) {
        (this.element as unknown as { msRequestFullscreen: () => void }).msRequestFullscreen();
      }
    } else if (this.document.exitFullscreen) {
      this.document.exitFullscreen().catch(() => undefined);
    }
  }

  changeLayout(layoutMode: string) {
    this.theme = layoutMode;
    this.layoutStore.changeLayout(layoutMode);
    document.documentElement.dataset['layout'] = layoutMode;
  }
  toggleResponsiveMenu() {
    document.body.classList.toggle('mobileMenuOpen');
  }
}
